import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, RefreshCcw, Trash2, MapPin } from "lucide-react";

const FIELDS = (agents, priceLists) => [
  { name: "name", label: "Ragione sociale", required: true, full: true },
  { name: "code", label: "Codice (vuoto = automatico)" },
  { name: "vat", label: "P.IVA / CF" },
  {
    name: "customer_type", label: "Tipologia", type: "select",
    options: [
      { value: "azienda", label: "Azienda" },
      { value: "privato", label: "Privato" },
      { value: "pa", label: "Pubblica amministrazione" },
      { value: "condominio", label: "Condominio" },
    ],
  },
  {
    name: "price_list_id", label: "Listino dedicato", type: "select",
    options: priceLists.map((p) => ({ value: p.id, label: `${p.name} (−${p.discount_pct || 0}%)` })),
  },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Telefono" },
  { name: "address", label: "Indirizzo", full: true },
  { name: "city", label: "Città" },
  { name: "province", label: "Provincia" },
  { name: "zip", label: "CAP" },
  { name: "lat", label: "Latitudine", type: "number" },
  { name: "lng", label: "Longitudine", type: "number" },
  {
    name: "agent_id", label: "Agente", type: "select",
    options: agents.map((a) => ({ value: a.id, label: a.name })),
  },
  { name: "notes", label: "Note", type: "textarea", full: true },
];

export default function Clienti() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/customers", { params: { search } }).then(({ data }) => setRows(data));

  useEffect(() => {
    load();
    if (user?.role === "azienda") api.get("/users", { params: { role: "agente" } }).then(({ data }) => setAgents(data));
    api.get("/price-lists").then(({ data }) => setPriceLists(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const save = async (values) => {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, values);
      else await api.post("/customers", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    await api.delete(`/customers/${id}`);
    load();
  };

  const push = async (id) => {
    await api.post(`/sync/customers/${id}/push`);
    load();
  };

  return (
    <div data-testid="clienti-page">
      <PageHeader title="Anagrafica clienti" subtitle={`${rows.length} clienti`}>
        <Input
          placeholder="Cerca cliente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-11 rounded-sm w-full sm:w-56"
          data-testid="clienti-search"
        />
        {user?.role === "azienda" && (
          <Button
            className="h-11 rounded-sm"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
            data-testid="new-customer-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Nuovo cliente
          </Button>
        )}
      </PageHeader>

      <ListCard testid="clienti-list">
        {rows.map((c) => (
          <div key={c.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold" data-testid={`customer-name-${c.id}`}>{c.name}</p>
                {c.be_id && (
                  <Badge variant="secondary" className="rounded-sm font-mono text-[10px]">
                    {c.be_id} · sincronizzato
                  </Badge>
                )}
                {c.price_list_id && (
                  <Badge variant="outline" className="rounded-sm text-[10px]">
                    {priceLists.find((p) => p.id === c.price_list_id)?.name || "listino dedicato"}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {c.address || "—"}, {c.city || "—"} · {c.phone || "—"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" className="rounded-sm">
                <Link to={`/impianti?customer=${c.id}`} data-testid={`customer-plants-${c.id}`}>Impianti</Link>
              </Button>
              {user?.role === "azienda" && (
                <>
                  <Button asChild variant="outline" size="sm" className="rounded-sm">
                    <Link to={`/contratti?customer=${c.id}`} data-testid={`customer-contracts-${c.id}`}>Contratti</Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => {
                      setEditing(c);
                      setOpen(true);
                    }}
                    data-testid={`edit-customer-${c.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => push(c.id)}
                          data-testid={`push-customer-${c.id}`} title="Invia a Business Experience">
                    <RefreshCcw className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                          onClick={() => del(c.id)} data-testid={`delete-customer-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun cliente trovato." />}
      </ListCard>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? "Modifica cliente" : "Nuovo cliente"}
        fields={FIELDS(agents, priceLists)}
        initial={editing || {}}
        onSubmit={save}
        testid="customer-dialog"
      />
    </div>
  );
}
